import { compose, map, sort } from 'ramda';
import {
    paramCompare,
    paramDsName,
    paramOrder,
    paramRow,
    paramType,
} from './constants/parameterNames';
import {
    typeDataSource,
    typeOption,
    typeXPath,
} from './constants/parameterTypes';
import { success } from './helpers/commandResult';
import { getParamValue } from './helpers/parameter.accessors';
import { getDataSource } from './helpers/qRules.dataSource.helpers';
import { getValidNode } from './helpers/qRules.node.helpers';

const DELIMETER = ',';
const DELIMETER_GUIDANCE =
    ' When sorting on multiple columns, separate values with commas.';

const ASC = 'asc';
const DESC = 'desc';

const TEXT = 'text';
const NUMBER = 'number';

const optionalParameters = [
    {
        name: paramDsName,
        description: 'Data Source Name',
        type: typeDataSource,
    },
    {
        name: paramOrder,
        description: `(Default: ${ASC}) Sort Order (${ASC} | ${DESC}).${DELIMETER_GUIDANCE}`,
        type: typeOption,
        options: [
            {
                name: 'Ascending',
                value: 'asc',
            },
            {
                name: 'Descending',
                value: 'desc',
            },
        ],
    },
    {
        name: paramType,
        description: `(Default: ${TEXT}) Data Type (${TEXT} | ${NUMBER}).${DELIMETER_GUIDANCE}`,
        type: typeOption,
        options: [
            {
                name: 'Text',
                value: 'text',
            },
            {
                name: 'Number',
                value: 'number',
            },
        ],
    },
];
const requiredParameters = [
    {
        name: paramRow,
        description: 'XPath to table rows to sort.',
        type: typeXPath,
        dsParentName: paramDsName,
    },
    {
        name: paramCompare,
        description:
            'XPath to field(s) to compare, relative to the rows being sorted.' +
            DELIMETER_GUIDANCE,
        type: typeXPath,
        dsParentName: paramDsName,
        relativeTo: paramRow,
    },
];
const errorFailedToSelectTable = 'Failed to select the table to sort.';
const errorFailedToSelectRow = 'Failed to select the row to sort.';
const errorSortFieldCountMismatch =
    'Parameter count mismatch for compare, order, and type parameters.' +
    '\nPlease verify each parameter has the same number of comma-separated values.';
const errorInvalidOrder = `The order parameter has an invalid value. This parameter, if included, should be set to ${ASC} or ${DESC}.`;
const errorInvalidDataType = `The type parameter has an invalid value. This parameter, if included, should be set to ${NUMBER} or ${TEXT}.`;
const description =
    'Sorts a table according to the specified column(s) and order(s).';

const sortNode = (xPath, isAsc, isNumber) => (node1, node2) => {
    const value1 = isNumber
        ? node1.evaluateNumber(xPath)
        : node1.evaluateString(xPath);
    const value2 = isNumber
        ? node2.evaluateNumber(xPath)
        : node2.evaluateString(xPath);

    if (value1 === value2) {
        return 0;
    }

    return value1 < value2 ? (isAsc ? -1 : 1) : isAsc ? 1 : -1;
};

function isAscOrder(orderType) {
    switch (orderType.toLowerCase()) {
        case 'desc':
            return false;
        case 'asc':
            return true;
        default:
            throw new Error(errorInvalidOrder);
    }
}

function isNumberType(dataType) {
    switch (dataType.toLowerCase()) {
        case 'text':
            return false;
        case 'number':
            return true;
        default:
            throw new Error(errorInvalidDataType);
    }
}

function applyMultipleSort(filters, orders, dataTypes) {
    // [Boolean]
    var isAscOrders = orders.map(isAscOrder);
    // [Boolean]
    var isNumbers = dataTypes.map(isNumberType);
    var filterLength = filters.length;

    return (node1, node2) => {
        let i = 0,
            result = 0;

        while (result === 0 && i < filterLength) {
            result = sortNode(
                filters[i],
                isAscOrders[i],
                isNumbers[i],
            )(node1, node2);

            i += 1;
        }

        return result;
    };
}

const getSortedNodes = (rowNodes, fieldXPaths, orders, dataTypes) =>
    compose(
        map((node) => node.getNode()),
        sort(applyMultipleSort(fieldXPaths, orders, dataTypes)),
    )(rowNodes);

async function sortTableAsync(
    dataSources,
    dsName,
    rowXPath,
    fieldXPathList,
    orderList,
    typeList,
) {
    const dom = getDataSource(dataSources, dsName);
    const table = getValidNode(dom, rowXPath + '/..', errorFailedToSelectTable);
    const fieldXPaths = fieldXPathList.split(DELIMETER);
    const orders = orderList.split(DELIMETER);
    const dataTypes = typeList.split(DELIMETER);

    // Ensure there is at least one row.
    getValidNode(dom, rowXPath, errorFailedToSelectRow);

    const lengthsMatch =
        fieldXPaths.length === orders.length &&
        orders.length === dataTypes.length;

    // Ensure each parameter has the same number of split values.
    if (!lengthsMatch) {
        throw new Error(errorSortFieldCountMismatch);
    }

    // Make sure we have 2 or more rows.
    const rowNodes = dom.selectNodes(rowXPath);
    if (rowNodes.length <= 1) {
        // No need to sort--not enough rows.
        return success();
    }

    const sortedNodes = getSortedNodes(
        rowNodes,
        fieldXPaths,
        orders,
        dataTypes,
    );

    await table.setContentAsync(sortedNodes);

    return success();
}

function executeAsync({ dataSources, parameters }) {
    const dsName = getParamValue(parameters, paramDsName);
    const row = getParamValue(parameters, paramRow);
    const compare = getParamValue(parameters, paramCompare);
    const order = getParamValue(parameters, paramOrder, ASC);
    const type = getParamValue(parameters, paramType, TEXT);

    return sortTableAsync(dataSources, dsName, row, compare, order, type);
}

const SortTable = {
    description,
    executeAsync,
    optionalParameters,
    requiredParameters,
};

export default SortTable;
